import React from 'react';

const printStyles = `
  @page { size: A4; margin: 10mm; }
  @media print {
    html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
    .voucher-print-wrapper { box-shadow: none !important; margin: 0 auto !important; width: 210mm !important; border: none !important; }
  }
`;

const formatINR = (value: any) => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : Number(value || 0);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

const numberToWords = (n: number): string => {
  if (!n || n === 0) return 'Rupees Zero Only';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const toW = (x: number): string => {
    if (x === 0) return '';
    if (x < 20) return ones[x] + ' ';
    if (x < 100) return tens[Math.floor(x / 10)] + (x % 10 ? ' ' + ones[x % 10] : '') + ' ';
    if (x < 1000) return ones[Math.floor(x / 100)] + ' Hundred ' + toW(x % 100);
    if (x < 100000) return toW(Math.floor(x / 1000)) + 'Thousand ' + toW(x % 1000);
    if (x < 10000000) return toW(Math.floor(x / 100000)) + 'Lakh ' + toW(x % 100000);
    return toW(Math.floor(x / 10000000)) + 'Crore ' + toW(x % 10000000);
  };
  return 'Rupees ' + toW(Math.floor(Math.abs(n))).trim() + ' Only';
};

const b = '1px solid #000';

const VoucherPrint = ({ data }: { data: any }) => {
  if (!data) return null;

  const company = data.company || {};
  const meta = data.meta || {};
  const entries = data.entries || [];
  const voucherType = (meta.voucherType || 'Voucher').toUpperCase();
  const isReceipt = voucherType.includes('RECEIPT');
  const isPayment = voucherType.includes('PAYMENT');
  const isContra = voucherType.includes('CONTRA');
  const isJournal = voucherType.includes('JOURNAL');

  const totalAmount = entries.reduce((s: number, e: any) => s + (parseFloat(e.debit) || 0), 0) || entries.reduce((s: number, e: any) => s + (parseFloat(e.credit) || 0), 0);

  const getTitle = () => {
    if (isReceipt) return 'RECEIPT VOUCHER';
    if (isPayment) return 'PAYMENT VOUCHER';
    if (isContra) return 'CONTRA VOUCHER';
    if (isJournal) return 'JOURNAL VOUCHER';
    return voucherType + ' VOUCHER';
  };

  return (
    <>
      <style>{printStyles}</style>
      <div className="voucher-print-wrapper mx-auto bg-white"
        style={{ width: '210mm', boxSizing: 'border-box', fontSize: '13px', lineHeight: 1.4, padding: '8px', fontFamily: 'Arial, sans-serif', color: '#000' }}>
        <div style={{ border: b, display: 'flex', flexDirection: 'column' }}>

          {/* Voucher Type Header */}
          <div style={{ borderBottom: b, textAlign: 'center', padding: '8px', fontWeight: 800, fontSize: '16px', letterSpacing: '3px', backgroundColor: '#f3f4f6' }}>
            {getTitle()}
          </div>

          {/* Company Header */}
          <div style={{ borderBottom: b, textAlign: 'center', padding: '10px 12px' }}>
            <div style={{ fontWeight: 800, fontSize: '20px', letterSpacing: '1px' }}>{company.name || ''}</div>
            {company.address && <div style={{ fontSize: '11px', marginTop: '2px' }}>{Array.isArray(company.address) ? company.address.join(', ') : company.address}</div>}
            <div style={{ fontSize: '11px', marginTop: '2px' }}>
              {company.gstin && <span>GSTIN: {company.gstin}</span>}
              {company.stateName && <span>, State: {company.stateName}</span>}
              {company.phone && <span>, Ph: {company.phone}</span>}
            </div>
          </div>

          {/* Voucher Details Row */}
          <div style={{ display: 'flex', borderBottom: b }}>
            <div style={{ width: '50%', padding: '8px 12px', borderRight: b }}>
              <div style={{ fontSize: '11px', color: '#666' }}>Voucher No.</div>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>{meta.invoiceNo || meta.voucherNo || ''}</div>
            </div>
            <div style={{ width: '50%', padding: '8px 12px' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>Date</div>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>{meta.date || ''}</div>
            </div>
          </div>

          {/* Receipt/Payment: Simple format */}
          {(isReceipt || isPayment) && (
            <>
              <div style={{ padding: '12px', borderBottom: b }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  {isReceipt ? 'Received From' : 'Paid To'}
                </div>
                <div style={{ fontWeight: 700, fontSize: '15px' }}>
                  {entries.find((e: any) => isReceipt ? parseFloat(e.debit) > 0 : parseFloat(e.credit) > 0)?.ledger || '—'}
                </div>
              </div>
              <div style={{ padding: '12px', borderBottom: b }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  {isReceipt ? 'On Account Of' : 'On Account Of'}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                  {entries.find((e: any) => isReceipt ? parseFloat(e.credit) > 0 : parseFloat(e.debit) > 0)?.ledger || '—'}
                </div>
              </div>
              <div style={{ display: 'flex', borderBottom: b, backgroundColor: '#f9fafb' }}>
                <div style={{ width: '60%', padding: '10px 12px', borderRight: b }}>
                  <div style={{ fontSize: '11px', color: '#666' }}>Amount (in words)</div>
                  <div style={{ fontWeight: 700, fontSize: '13px', fontStyle: 'italic' }}>{numberToWords(totalAmount)}</div>
                </div>
                <div style={{ width: '40%', padding: '10px 12px', textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: '#666' }}>Amount</div>
                  <div style={{ fontWeight: 800, fontSize: '18px' }}>₹ {formatINR(totalAmount)}</div>
                </div>
              </div>
            </>
          )}

          {/* Journal/Contra: Dr-Cr Table format */}
          {(isJournal || isContra || (!isReceipt && !isPayment)) && (
            <>
              {/* Column Headers */}
              <div style={{ display: 'flex', borderBottom: b, backgroundColor: '#f3f4f6', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <div style={{ width: '50%', padding: '6px 12px', borderRight: b }}>Particulars</div>
                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'right', borderRight: b }}>Debit (₹)</div>
                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'right' }}>Credit (₹)</div>
              </div>
              {/* Entry Rows */}
              {entries.map((entry: any, idx: number) => (
                <div key={idx} style={{ display: 'flex', borderBottom: b, fontSize: '12px' }}>
                  <div style={{ width: '50%', padding: '5px 12px', borderRight: b, fontWeight: parseFloat(entry.debit) > 0 ? 600 : 400, paddingLeft: parseFloat(entry.credit) > 0 ? '28px' : '12px' }}>
                    {parseFloat(entry.credit) > 0 ? 'To ' : ''}{entry.ledger || ''}
                  </div>
                  <div style={{ width: '25%', padding: '5px 12px', textAlign: 'right', borderRight: b, fontWeight: 600 }}>
                    {parseFloat(entry.debit) > 0 ? formatINR(entry.debit) : ''}
                  </div>
                  <div style={{ width: '25%', padding: '5px 12px', textAlign: 'right', fontWeight: 600 }}>
                    {parseFloat(entry.credit) > 0 ? formatINR(entry.credit) : ''}
                  </div>
                </div>
              ))}
              {/* Empty rows */}
              {entries.length < 4 && Array.from({ length: 4 - entries.length }).map((_, i) => (
                <div key={`e${i}`} style={{ display: 'flex', borderBottom: b, minHeight: '22px' }}>
                  <div style={{ width: '50%', borderRight: b }}>&nbsp;</div>
                  <div style={{ width: '25%', borderRight: b }}>&nbsp;</div>
                  <div style={{ width: '25%' }}>&nbsp;</div>
                </div>
              ))}
              {/* Totals */}
              <div style={{ display: 'flex', borderBottom: b, backgroundColor: '#f3f4f6', fontWeight: 700, fontSize: '13px' }}>
                <div style={{ width: '50%', padding: '6px 12px', borderRight: b }}>Total</div>
                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'right', borderRight: b }}>
                  {formatINR(entries.reduce((s: number, e: any) => s + (parseFloat(e.debit) || 0), 0))}
                </div>
                <div style={{ width: '25%', padding: '6px 12px', textAlign: 'right' }}>
                  {formatINR(entries.reduce((s: number, e: any) => s + (parseFloat(e.credit) || 0), 0))}
                </div>
              </div>
              {/* Amount in words */}
              <div style={{ borderBottom: b, padding: '6px 12px' }}>
                <div style={{ fontSize: '10px', color: '#666' }}>Amount (in words):</div>
                <div style={{ fontWeight: 700, fontSize: '12px', fontStyle: 'italic' }}>{numberToWords(totalAmount)}</div>
              </div>
            </>
          )}

          {/* Narration */}
          {meta.narration && (
            <div style={{ borderBottom: b, padding: '8px 12px' }}>
              <div style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>Narration:</div>
              <div style={{ fontSize: '12px' }}>{meta.narration}</div>
            </div>
          )}

          {/* Signature Block */}
          <div style={{ display: 'flex', minHeight: '70px' }}>
            <div style={{ width: '50%', padding: '8px 12px', borderRight: b }}>
              <div style={{ fontSize: '11px', color: '#666' }}>
                {isReceipt ? "Receiver's Signature" : isPayment ? "Receiver's Signature" : "Prepared By"}
              </div>
            </div>
            <div style={{ width: '50%', padding: '8px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '12px' }}>For: {company.name || ''}</div>
              <div style={{ textAlign: 'right', fontSize: '10px', fontWeight: 600, borderTop: '1px solid #ccc', paddingTop: '4px', marginTop: 'auto' }}>Authorized Signatory</div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default VoucherPrint;
